import {
  Injectable,
  OnDestroy,
  Optional,
  SecurityContext,
} from '@angular/core';
import {
  DomSanitizer,
} from '@angular/platform-browser';
import {
  formatCurrencyValue,
} from '@dapi/sdk/helpers';
import {
  CartModel,
  CartStore,
  selectCurrentCart,
} from '@des/store/cart';
import {
  OrderModel,
  OrderStore,
  selectCurrentOrder,
} from '@des/store/order';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  ConfigurationBaseService,
  ConfigurationObserver,
} from '@o3r/configuration';
import {
  getCartTotalPrice,
  getOrderTotalPrice,
  isBookingFlow,
} from '@refx/shared-common';
import {
  selectFlowType,
  selectIsRewardFlow,
  UiStatusStore,
} from '@refx/shared-store';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import {
  EVENT_CONVERSION,
  EVENT_QUALITY,
  METACONNECT_BASE_PATH,
  METACONNECT_DOM_SCRIPT_ID,
  METACONNECT_FUNCTION_NAME,
} from './constants';
import {
  META_CONNECT_CONFIG_ID,
  META_CONNECT_DEFAULT_CONFIG,
  MetaConnectConfig,
} from './metaconnect.config';
import {
  MetaConnectModule,
} from './metaconnect.module';

@Injectable({
  providedIn: MetaConnectModule
})
export class MetaConnectService implements OnDestroy {
  /**
   * Configuration of the Meta Connect service
   */
  protected config$: Observable<MetaConnectConfig>;

  /**
   * Configuration observer for the Meta Connect service
   */
  protected dynamicConfig$: ConfigurationObserver<MetaConnectConfig>;

  /**
   * List of subscriptions to unsubscribe on destroy
   */
  protected subscriptions: Subscription[] = [];

  /**
   * Observable that returns true if we should perform the calls to metaconnect
   * e.g. this is activated AND we are in the booking flow AND we are not in the reward flow
   */
  protected canActivate$: Observable<boolean>;

  /**
   * Observable of the current order
   */
  protected currentOrder$: Observable<OrderModel | null>;

  /**
   * Observable of the current cart
   */
  protected currentCart$: Observable<CartModel | null>;

  constructor(
    protected readonly store: Store<UiStatusStore & CartStore & OrderStore>,
    protected domSanitizer: DomSanitizer,
    @Optional() configurationService?: ConfigurationBaseService
  ) {
    this.dynamicConfig$ = new ConfigurationObserver<MetaConnectConfig>(META_CONNECT_CONFIG_ID, META_CONNECT_DEFAULT_CONFIG, configurationService);
    this.config$ = this.dynamicConfig$.asObservable();

    this.canActivate$ = combineLatest([
      this.config$.pipe(filter((config) => !!config)),
      this.store.pipe(select(selectFlowType)).pipe(filter((flowType) => !!flowType)),
      this.store.pipe(select(selectIsRewardFlow))
    ]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(([config, flowType, isRewardFlow]) => config.enableMetaConnect && isBookingFlow(flowType) && !isRewardFlow)
    );

    this.currentCart$ = this.store.pipe(select(selectCurrentCart));
    this.currentOrder$ = this.store.pipe(select(selectCurrentOrder));

    this.subscriptions.push(
      combineLatest([
        this.canActivate$,
        this.config$.pipe(
          filter((config) => !!config),
          map((config) => config.airlineCode))
      ]).pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      ).subscribe(([canActivate, airlineCode]) => {
        if (canActivate && !!airlineCode) {
          const sanitizedUrlScript = this.domSanitizer.sanitize(SecurityContext.RESOURCE_URL,
            this.domSanitizer.bypassSecurityTrustResourceUrl(METACONNECT_BASE_PATH + airlineCode + '.js')) || '';
          if (!this.isReady()) {
            this.loadScript(sanitizedUrlScript);
          }
        } else {
          this.removeScript();
        }
      })
    );
  }

  /**
   * Get a pointer to the metaconnect script loaded on the DOM.
   *
   * @returns a reference ot the tac script loaded on the DOM
   */
  protected getScriptFn() {
    const fn = (window as any)[METACONNECT_FUNCTION_NAME];
    if (!!fn && typeof (fn) === 'function') {
      return fn;
    }
  }

  /**
   * Call the Meta Connect script function if it exists
   *
   * @param args list of arguments to be passed to the function
   */
  protected callScriptFn(...args: any[]): void {
    const fn = this.getScriptFn();
    if (fn) {
      fn(...args);
    }
  }

  /**
   * Check if Meta Connect is already activated by checking if the "tac" object exists
   *
   * @returns {boolean} true if Meta Connect is already activated
   */
  protected isReady(): boolean {
    return !!this.getScriptFn();
  }

  /**
   * Load the Meta Connect script according to the airline code defined in the configuration
   *
   * @param sanitizedUrlScript the metaconnect script url that we need to load
   */
  protected loadScript(sanitizedUrlScript: string): Element | null {
    const element = document.createElement('script');
    element.setAttribute('language', 'javascript');
    element.setAttribute('type', 'text/javascript');
    element.setAttribute('async', 'true');
    element.setAttribute('id', METACONNECT_DOM_SCRIPT_ID);
    element.src = sanitizedUrlScript;
    return document.body.insertAdjacentElement('afterbegin', element);
  }

  /**
   * Removes the Meta Connect script if it is present in the DOM
   * When starting MMB flow for example, the flow type goes from booking to mmb so the script
   * is loaded in the first place (because flow type is booking) even if we don't want to.
   */
  protected removeScript(): void {
    document.getElementById(METACONNECT_DOM_SCRIPT_ID)?.remove();

    // Remove the script function from the global scope
    delete (window as any)[METACONNECT_FUNCTION_NAME];
  }

  /**
   * Send quality hit to Meta Connect infrastucture.
   * We send it only when we have a cart but not yet an order
   */
  public sendQualityTracking() {
    if (!this.isReady()) {
      return;
    }

    this.subscriptions.push(
      combineLatest([
        this.currentCart$,
        this.currentOrder$,
        this.canActivate$
      ]).pipe(
        filter(([cart, order, canActivate]) => canActivate && !!cart && !order && !!getCartTotalPrice(cart)),
        map(([cart]) => cart),
        take(1)
      ).subscribe((cart) => {
        // Get only the total price of the flights (if we have services, we will not consider them)
        const totalPrice = cart!.airOffers?.[0]?.offerItems?.[0]?.prices?.totalPrices?.[0];
        const bounds = cart?.airOffers?.[0].offerItems[0].air.bounds;

        if (bounds) {
          /* flightSegmentList looks like this (each element represents a flight segment) :
            [{
              "flightNumber" : "AZ12344",
              "fareFamily" : "FF1"
            },
            {
              "flightNumber" : "AZ554",
              "fareFamily" : "FF3"
            }]
          */
          const flightSegmentList: {fareFamily: string | undefined;
            flightNumber: string;}[] = [];
          for (const bound of bounds) {
            for (const segment of bound.flights) {
              const flightSegment = {
                'fareFamily': segment.fareFamilyCode,
                'flightNumber': segment.flight.marketingAirlineCode + segment.flight.marketingFlightNumber
              };
              flightSegmentList.push(flightSegment);
            }
          }

          this.callScriptFn(EVENT_QUALITY,
            formatCurrencyValue(totalPrice!.total, totalPrice!.currency),
            totalPrice!.currencyCode,
            flightSegmentList);
        }
      })
    );
  }

  /**
   * Call to a Meta Connect function to handle the tracking of conversion
   * We send it from the confirmation page
   */
  public sendConversionTracking() {
    if (!this.isReady()) {
      return;
    }

    this.subscriptions.push(
      combineLatest([
        this.currentOrder$.pipe(filter((order) => !!order)),
        this.canActivate$
      ]).pipe(
        filter(([order, canActivate]) => canActivate && !!order),
        take(1)
      ).subscribe(([order]) => {
        if (order) {
          const totalPrice = getOrderTotalPrice(order, true)?.total;

          if (totalPrice) {
            this.callScriptFn(EVENT_CONVERSION,
              formatCurrencyValue(totalPrice.value, totalPrice.currency),
              totalPrice.currencyCode,
              order.id);
          }
        }
      })
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
